import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { KostenMaandKeuze } from '@/components/KostenMaandKeuze';
import { CashDateField } from '@/components/cash/CashDateField';
import { localDate } from '@/lib/cashReceipts';
import { useSearchParams } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { FileXls, FilePdf, Wallet, Plus, Trash, PencilSimple, X, FunnelSimple, TrendUp, TrendDown, Clock, CheckCircle, XCircle, ArrowsClockwise, Paperclip, DownloadSimple, UploadSimple, Bookmark, CalendarBlank, LinkSimple, Sparkle, CircleNotch, Eye, Warning } from '@phosphor-icons/react';
import { KostInkomendKoppel } from '@/components/KostInkomendKoppel';

/**
 * Kostenrapport — bedrijfskosten per maand (huur, water, personeel, RSZ, ...)
 * Alles excl. BTW. Enkel admin-toegang. Gekoppeld aan verkoop-winst voor netto resultaat.
 */
export default function KostenRapportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const today = new Date();
  const eerste = new Date(today.getFullYear(), today.getMonth(), 1);
  const ymd = localDate;

  // Deep-link vanuit Inkomende documenten: ?kost=ID&datum=YYYY-MM-DD → maand van de factuurdatum tonen + rij markeren
  const [searchParams] = useSearchParams();
  const focusKostId = searchParams.get('kost') || '';
  const focusDatum = searchParams.get('datum') || '';
  const focusMaand = /^\d{4}-\d{2}-\d{2}$/.test(focusDatum) ? focusDatum.slice(0, 7) : '';
  const laatsteDag = (ym) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`; };
  const [van, setVan] = useState(focusMaand ? `${focusMaand}-01` : ymd(eerste));
  const [tot, setTot] = useState(focusMaand ? laatsteDag(focusMaand) : ymd(today));
  const [categorie, setCategorie] = useState('');
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadVersion = useRef(0);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catHint, setCatHint] = useState(null);
  // Approval state
  const [wachtend, setWachtend] = useState([]);
  const [approving, setApproving] = useState(null);  // {id, bedrag_excl, datum, omschrijving, ...}
  const [aiUpload, setAiUpload] = useState(null);  // {index, totaal, naam} tijdens AI-lezen van geüploade facturen

  const [form, setForm] = useState({
    datum: ymd(today),
    categorie: 'huur',
    omschrijving: '',
    leverancier: '',
    bedrag_excl: '',
    bedrag_incl: '',
    bedrag_basis: 'excl',
    btw_tarief: 21,
    betaald: false,
    betaaldatum: '',
    referentie: '',
    aankoop_factuur_id: '',
    inkomend_doc_id: '',
    commentaar: '',
    terugkerend: false,
    terugkerend_interval: 'maandelijks',
    terugkerend_dag: 1,
    terugkerend_uur: 6,
  });

  // ── Vaste-kost sjablonen (V26) ──
  const monthPrefix = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const [sjablonen, setSjablonen] = useState([]);
  const [showBoekModal, setShowBoekModal] = useState(false);
  const [boekMaand, setBoekMaand] = useState(monthPrefix(today));
  const [boekDatum, setBoekDatum] = useState('');  // leeg = 1e dag van maand
  const [boekSelectie, setBoekSelectie] = useState({});  // {sjabloon_id: {checked, bedrag_override}}
  const [boekBusy, setBoekBusy] = useState(false);
  const [showSjabloonForm, setShowSjabloonForm] = useState(false);
  const [sjabloonForm, setSjabloonForm] = useState({ id: null, naam: '', categorie: 'huur', leverancier: '', bedrag_excl_default: '', btw_tarief: 21, sort_order: 100, actief: true, notitie: '' });

  const allCats = useMemo(() => {
    const std = cats.standaard || [];
    const custom = cats.custom || [];
    return [...std, ...custom];
  }, [cats]);

  useEffect(() => {
    if (!isAdmin) return;
    API.get('/kosten-categorieen').then(r => setCats(r.data || { standaard: [], custom: [] })).catch(() => {});
  }, [isAdmin]);

  const loadWachtend = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await API.get('/kosten/wachtend');
      setWachtend(r.data || []);
    } catch { /* niks */ }
  }, [isAdmin]);

  useEffect(() => { loadWachtend(); }, [loadWachtend]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    const version = ++loadVersion.current;
    if (!van || !tot || van > tot) { setData({ items: [], fout: 'Kies een geldige begin- en einddatum.' }); setLoading(false); return; }
    setLoading(true);
    try {
      const params = { van, tot };
      if (categorie) params.categorie = categorie;
      const r = await API.get('/kosten-rapport', { params });
      if (version === loadVersion.current) setData(r.data);
    } catch (e) {
      if (version === loadVersion.current) setData({ items: [], fout: e.response?.data?.detail || e.message });
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [van, tot, categorie, isAdmin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusKostId || !data?.items) return;
    const el = document.querySelector(`[data-testid="kost-row-${focusKostId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusKostId, data]);

  // ── Vaste-kost sjablonen laden + boek-modal handlers ──
  const loadSjablonen = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await API.get('/vaste-kost-sjablonen');
      setSjablonen(Array.isArray(r.data) ? r.data : []);
    } catch { /* stil */ }
  }, [isAdmin]);
  useEffect(() => { loadSjablonen(); }, [loadSjablonen]);

  const openBoekModal = () => {
    // Initialiseer selectie: alle ACTIEVE sjablonen aangevinkt met standaard-bedrag
    const sel = {};
    sjablonen.filter(s => s.actief).forEach(s => { sel[s.id] = { checked: true, bedrag_override: '' }; });
    setBoekSelectie(sel);
    setBoekDatum('');
    setShowBoekModal(true);
  };

  const toggleSjabloonCheck = (sid) => {
    setBoekSelectie(p => ({ ...p, [sid]: { checked: !p[sid]?.checked, bedrag_override: p[sid]?.bedrag_override || '' } }));
  };
  const setSjabloonBedrag = (sid, val) => {
    setBoekSelectie(p => ({ ...p, [sid]: { checked: p[sid]?.checked ?? true, bedrag_override: val } }));
  };

  const boekVasteKosten = async () => {
    const items = sjablonen
      .filter(s => boekSelectie[s.id]?.checked)
      .map(s => ({
        sjabloon_id: s.id,
        bedrag_excl: boekSelectie[s.id].bedrag_override !== '' ? parseFloat(boekSelectie[s.id].bedrag_override) : s.bedrag_excl_default,
      }));
    if (items.length === 0) { toast.warning('Selecteer minstens één sjabloon'); return; }
    setBoekBusy(true);
    try {
      const r = await API.post('/vaste-kost-sjablonen/boek-maand', {
        maand: boekMaand,
        datum: boekDatum || undefined,
        items,
      });
      const d = r.data;
      const aangemaakt = d.aangemaakt?.length || 0;
      const overgeslagen = d.overgeslagen?.length || 0;
      if (aangemaakt > 0) {
        toast.success(`${aangemaakt} vaste kost(en) geboekt voor ${boekMaand} · Totaal € ${(d.totaal_bedrag_excl || 0).toFixed(2)}${overgeslagen > 0 ? ` (${overgeslagen} overgeslagen: al geboekt)` : ''}`);
      } else if (overgeslagen > 0) {
        toast.info(`Niets aangemaakt — alle ${overgeslagen} sjabloon(en) waren al geboekt voor ${boekMaand}`);
      }
      setShowBoekModal(false);
      load(); loadWachtend();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij boeken');
    } finally { setBoekBusy(false); }
  };

  const saveSjabloon = async () => {
    if (!sjabloonForm.naam?.trim()) { toast.warning('Naam is verplicht'); return; }
    try {
      if (sjabloonForm.id) {
        await API.put(`/vaste-kost-sjablonen/${sjabloonForm.id}`, sjabloonForm);
      } else {
        await API.post('/vaste-kost-sjablonen', sjabloonForm);
      }
      setShowSjabloonForm(false);
      setSjabloonForm({ id: null, naam: '', categorie: 'huur', leverancier: '', bedrag_excl_default: '', btw_tarief: 21, sort_order: 100, actief: true, notitie: '' });
      loadSjablonen();
      toast.success('Sjabloon opgeslagen');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fout bij opslaan'); }
  };

  const deleteSjabloon = async (sid) => {
    if (!window.confirm('Sjabloon verwijderen? Bestaande geboekte kosten blijven behouden.')) return;
    try {
      await API.delete(`/vaste-kost-sjablonen/${sid}`);
      loadSjablonen();
      toast.success('Sjabloon verwijderd');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fout bij verwijderen'); }
  };

  const resetForm = () => {
    setForm({
      datum: ymd(today), categorie: 'huur', omschrijving: '', leverancier: '',
      bedrag_excl: '', bedrag_incl: '', bedrag_basis: 'excl', btw_tarief: 21, betaald: false, betaaldatum: '',
      referentie: '', aankoop_factuur_id: '', inkomend_doc_id: '', commentaar: '',
      terugkerend: false, terugkerend_interval: 'maandelijks',
      terugkerend_dag: 1, terugkerend_uur: 6,
    });
    setEditing(null);
  };

  const openNieuw = () => { resetForm(); setCatHint(null); setShowForm(true); };

  const openBewerk = (k) => {
    setEditing(k); setCatHint(null);
    setForm({
      datum: k.datum,
      categorie: k.categorie,
      omschrijving: k.omschrijving || '',
      leverancier: k.leverancier || '',
      bedrag_excl: k.bedrag_excl,
      bedrag_incl: k.bedrag_incl ?? '',
      bedrag_basis: k.bedrag_basis === 'incl' ? 'incl' : 'excl',
      btw_tarief: k.btw_tarief,
      betaald: !!k.betaald,
      betaaldatum: k.betaaldatum || '',
      referentie: k.referentie || '',
      aankoop_factuur_id: k.aankoop_factuur_id || '',
      inkomend_doc_id: k.inkomend_doc_id || '',
      commentaar: k.commentaar || '',
      terugkerend: !!k.terugkerend,
      terugkerend_interval: k.terugkerend_interval || 'maandelijks',
      terugkerend_dag: k.terugkerend_dag || 1,
      terugkerend_uur: k.terugkerend_uur ?? 6,
    });
    setShowForm(true);
  };

  // Gegevens van gekoppelde inkomende factuur overnemen (factuurdatum altijd leidend)
  const neemFactuurOver = (d) => {
    if (d.categorie_bron === 'geleerd' && d.categorie) setCatHint({ categorie: d.categorie, aantal: d.categorie_geleerd_aantal || 1, leverancier: d.leverancier, toegepast: !editing });
    setForm(p => ({
      ...p,
      inkomend_doc_id: d.inkomend_doc_id,
      datum: d.datum || p.datum,
      leverancier: d.leverancier || p.leverancier,
      referentie: d.referentie || p.referentie,
      omschrijving: p.omschrijving || d.omschrijving || (d.leverancier ? `Factuur ${d.leverancier}${d.referentie ? ` ${d.referentie}` : ''}` : ''),
      categorie: !editing && d.categorie ? d.categorie : p.categorie,
      btw_tarief: d.btw_tarief ?? p.btw_tarief,
      bedrag_basis: d.bedrag_basis,
      bedrag_excl: d.bedrag_basis === 'excl' && d.bedrag != null ? d.bedrag : p.bedrag_excl,
      bedrag_incl: d.bedrag_basis === 'incl' && d.bedrag != null ? d.bedrag : p.bedrag_incl,
    }));
  };

  // Geleerde categorie per leverancier (uit eerdere kosten)
  const zoekGeleerdeCategorie = async (lev) => {
    const naam = (lev || '').trim();
    if (naam.length < 2) { setCatHint(null); return; }
    try {
      const r = await API.get('/kosten/categorie-suggestie', { params: { leverancier: naam } });
      if (!r.data?.categorie) { setCatHint(null); return; }
      setCatHint({ ...r.data, toegepast: !editing && r.data.categorie !== form.categorie });
      if (!editing) setForm(p => ({ ...p, categorie: r.data.categorie }));
    } catch { setCatHint(null); }
  };

  const bedragIngevoerd = form.bedrag_basis === 'incl' ? form.bedrag_incl : form.bedrag_excl;
  const bedragExclBerekend = form.bedrag_basis === 'incl'
    ? (parseFloat(form.bedrag_incl) || 0) / (1 + (parseFloat(form.btw_tarief) || 0) / 100)
    : (parseFloat(form.bedrag_excl) || 0);
  const bedragInclBerekend = form.bedrag_basis === 'incl'
    ? (parseFloat(form.bedrag_incl) || 0)
    : (parseFloat(form.bedrag_excl) || 0) * (1 + (parseFloat(form.btw_tarief) || 0) / 100);

  const saveKost = async () => {
    if (!form.omschrijving.trim()) { toast.error('Omschrijving is verplicht'); return; }
    if (!bedragIngevoerd || parseFloat(bedragIngevoerd) <= 0) { toast.error(`Bedrag ${form.bedrag_basis === 'incl' ? 'incl.' : 'excl.'} BTW moet > 0 zijn`); return; }
    try {
      const payload = {
        ...form,
        bedrag_excl: parseFloat(form.bedrag_excl) || 0,
        bedrag_incl: parseFloat(form.bedrag_incl) || 0,
        btw_tarief: parseFloat(form.btw_tarief || 0),
      };
      if (editing) {
        await API.put(`/kosten/${editing.id}`, payload);
        toast.success('Kost bijgewerkt');
      } else {
        await API.post('/kosten', payload);
        toast.success('Kost geregistreerd');
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deleteKost = async (k) => {
    if (!window.confirm(`Kost verwijderen: ${k.omschrijving}?`)) return;
    try {
      await API.delete(`/kosten/${k.id}`);
      toast.success('Verwijderd');
      load();
    } catch (e) { toast.error('Verwijderen mislukt'); }
  };

  const uploadBestand = async (k, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await API.post(`/kosten/${k.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Bestand toegevoegd: ${file.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload mislukt');
    }
  };

  const downloadBestand = async (k) => {
    try {
      const res = await API.get(`/kosten/${k.id}/bestand`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = k.filename || `kost_${k.id}.${k.type || 'pdf'}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download mislukt');
    }
  };

  const verwijderBestand = async (k) => {
    if (!window.confirm(`Bestand "${k.filename}" verwijderen? De kost zelf blijft staan.`)) return;
    try {
      await API.delete(`/kosten/${k.id}/bestand`);
      toast.success('Bestand verwijderd');
      load();
    } catch (e) { toast.error('Verwijderen mislukt'); }
  };

  // Goedkeur-flow voor concept-kosten
  const openApproval = (k) => {
    setApproving({
      ...k,
      bedrag_excl: k.bedrag_excl,
      btw_tarief: k.btw_tarief,
      datum: k.datum,
      omschrijving: k.omschrijving,
      leverancier: k.leverancier || '',
      categorie: k.categorie || 'overig',
      referentie: k.referentie || '',
      betaald: !!k.betaald,
    });
  };
  const approveKost = async () => {
    if (!approving) return;
    if (!approving.bedrag_excl || parseFloat(approving.bedrag_excl) <= 0) { toast.error('Vul het bedrag excl. BTW in vóór goedkeuren'); return; }
    if (approving.dubbel_van && !window.confirm(`Mogelijk dubbel: ${approving.dubbel_van.leverancier} ${approving.dubbel_van.factuurnummer || ''} bestaat al als ${approving.dubbel_van.bron === 'kost' ? 'kost' : 'inkomende factuur'}.\n\nToch goedkeuren?`)) return;
    try {
      await API.post(`/kosten/${approving.id}/goedkeuren`, {
        bedrag_excl: parseFloat(approving.bedrag_excl),
        btw_tarief: parseFloat(approving.btw_tarief || 0),
        datum: approving.datum,
        omschrijving: approving.omschrijving,
        leverancier: approving.leverancier,
        categorie: approving.categorie,
        referentie: approving.referentie,
        betaald: approving.betaald,
        bevestig_dubbel: !!approving.dubbel_van,
      });
      toast.success(`✓ Goedgekeurd: ${approving.omschrijving}`);
      setApproving(null);
      await Promise.all([loadWachtend(), load()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Goedkeuren mislukt');
    }
  };
  const rejectKost = async (k) => {
    if (!window.confirm(`Afwijzen (= verwijderen): ${k.omschrijving}?`)) return;
    try {
      await API.post(`/kosten/${k.id}/afwijzen`);
      toast.success('Afgewezen');
      await loadWachtend();
    } catch (e) { toast.error('Afwijzen mislukt'); }
  };

  // Factuur uploaden → AI leest direct → concept-kost klaar om goed te keuren
  const uploadMetAI = async (files) => {
    const lijst = Array.from(files || []);
    if (!lijst.length) return;
    const nieuw = [];
    for (let i = 0; i < lijst.length; i++) {
      const f = lijst[i];
      setAiUpload({ index: i + 1, totaal: lijst.length, naam: f.name });
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await API.post('/kosten/upload-ai', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        nieuw.push(r.data);
        if(r.data.boekmap && r.data.boekmap!=='kosten')toast.info('Aankoop goederen/investering staat bij de goed te keuren inkomende facturen, niet bij de kosten');
        if (r.data?.dubbel) toast.warning(`Opgelet: ${r.data.dubbel.leverancier} ${r.data.dubbel.factuurnummer || ''} bestaat al als ${r.data.dubbel.bron === 'kost' ? 'kost' : 'inkomende factuur'} — mogelijk dubbel`, { duration: 8000 });
        if (r.data?.ai_gelezen) toast.success(`AI heeft "${f.name}" gelezen — klaar om goed te keuren`);
        else toast.warning(`"${f.name}" opgeslagen, maar AI kon de factuur niet lezen — vul de gegevens aan bij goedkeuren`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'Upload mislukt'}`);
      }
    }
    setAiUpload(null);
    await loadWachtend();
    if (nieuw.length === 1 && nieuw[0]?.kost) openApproval(nieuw[0].kost);
  };

  const bekijkDocument = async (k) => {
    try {
      const res = await API.get(`/kosten/${k.id}/bestand`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Document openen mislukt');
    }
  };

  const exportFile = async (format) => {
    if (!van || !tot || van > tot) { toast.error('Kies een geldige begin- en einddatum.'); return; }
    try {
      const params = new URLSearchParams({ van, tot });
      if (categorie) params.set('categorie', categorie);
      // Gebruik API.get met responseType:'blob' zodat JWT-header meegestuurd wordt
      // (window.open kan geen Authorization-header zetten → 401)
      const res = await API.get(`/kosten-rapport/${format}?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = `kostenrapport_${van}_${tot}.${ext}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success(`${format === 'excel' ? 'Excel' : 'PDF'} gedownload`);
    } catch (e) {
      // Probeer detail uit blob-error te halen
      let detail = e?.message || 'Export mislukt';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || detail;
        } catch { /* niks */ }
      } else {
        detail = e?.response?.data?.detail || detail;
      }
      toast.error(`${format === 'excel' ? 'Excel' : 'PDF'}-export mislukt: ${detail}`);
    }
  };

  const catLabel = (code) => {
    const c = allCats.find(x => x.code === code);
    return c ? `${c.icon || ''} ${c.label}`.trim() : code;
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-300 p-4 rounded">
          <div className="font-bold text-red-900">Geen toegang</div>
          <div className="text-sm text-red-700 mt-1">Enkel admin-gebruikers kunnen het kostenrapport bekijken.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Wallet size={22} className="text-[#002FA7]" weight="duotone" />
        <h1 className="text-xl font-bold text-gray-900">Kostenrapport</h1>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 border border-red-300 text-[10px] font-bold uppercase tracking-wider" data-testid="intern-badge">
          🔒 Intern
        </span>
        <span className="text-xs text-gray-500">Bedrijfskosten per maand (excl. BTW) · netto resultaat · niet voor externe distributie</span>
      </div>

      {/* Filter-balk */}
      <div className="bg-white border border-gray-200 p-3 mb-4" data-testid="kosten-rapport-filters">
        <div className="flex items-center gap-2 mb-2">
          <FunnelSimple size={14} className="text-gray-500" />
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Filters & acties</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 [&_input]:w-full [&_select]:w-full [&>div]:min-w-0">
          <div>
            <label className="text-[10px] font-bold text-gray-600 uppercase">Van</label>
            <CashDateField value={van} onChange={setVan} id="kosten-van" compact />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-600 uppercase">Tot</label>
            <CashDateField value={tot} onChange={setTot} id="kosten-tot" compact />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase">Categorie</label>
            <select className="field-input text-xs" value={categorie} onChange={e => setCategorie(e.target.value)} data-testid="kosten-cat-filter">
              <option value="">Alle categorieen</option>
              {allCats.map(c => <option key={c.code} value={c.code}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase">Maand / jaar</label>
            <KostenMaandKeuze van={van} tot={tot} onChange={(v,t)=>{setVan(v);setTot(t);}} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 mt-3 gap-2 [&>.btn]:w-full [&>.btn]:min-h-9 [&>.btn]:justify-center [&>.btn]:text-center [&>.btn]:whitespace-normal">
          <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => exportFile('excel')} data-testid="kosten-export-xlsx"><FileXls size={14} /> Excel</button>
          <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={() => exportFile('pdf')} data-testid="kosten-export-pdf"><FilePdf size={14} /> PDF</button>
          <label
            className={`btn btn-sm inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold ${aiUpload ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
            title="Upload één of meer facturen (PDF/foto). AI leest leverancier, datum, factuurnummer en bedragen; u hoeft enkel nog goed te keuren."
            data-testid="kosten-upload-ai"
          >
            {aiUpload ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />} Factuur uploaden → AI leest
            <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.heic,.xml" className="hidden" disabled={!!aiUpload} onChange={e => { uploadMetAI(e.target.files); e.target.value = ''; }} data-testid="kosten-upload-ai-input" />
          </label>
          <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={openBoekModal} data-testid="kosten-boek-vaste" title="Boek meerdere vaste kosten (huur, gas, lonen, ...) in één klik voor een specifieke maand">
            <CalendarBlank size={14} weight="bold" /> Boek vaste kosten voor maand…
          </button>
          <button className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={openNieuw} data-testid="kosten-nieuw"><Plus size={14} weight="bold" /> Nieuwe kost</button>
        </div>
      </div>

      {aiUpload && (
        <div className="bg-[#002FA7] text-white p-3 mb-4 flex items-center gap-3" data-testid="kosten-ai-bezig">
          <CircleNotch size={22} className="animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">AI leest factuur {aiUpload.index}/{aiUpload.totaal}…</div>
            <div className="text-[11px] opacity-80 truncate">{aiUpload.naam} — leverancier, factuurnummer, datum en bedragen worden automatisch herkend. U hoeft enkel nog goed te keuren.</div>
          </div>
        </div>
      )}

      {loading && <div className="text-center py-8 text-gray-500">Laden...</div>}
      {data?.fout && !loading && <div className="bg-red-50 border border-red-200 p-3 mb-4 text-sm text-red-800" role="alert" data-testid="kosten-report-error">{data.fout}</div>}

      {/* Wachtend op goedkeuring — concept-kosten (sjablonen-cron of AI-gelezen uploads) */}
      {wachtend.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-400 p-3 mb-4" data-testid="kosten-wachtend-banner">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-amber-700" weight="fill" />
            <span className="font-bold text-amber-900 text-sm">
              {wachtend.length} kost{wachtend.length > 1 ? 'en' : ''} wacht{wachtend.length === 1 ? '' : 'en'} op goedkeuring
            </span>
            <span className="text-[11px] text-amber-700">(automatisch aangemaakt — uit vaste-kost sjablonen of AI-gelezen facturen — controleer en keur goed)</span>
          </div>
          <div className="space-y-1.5">
            {wachtend.map(w => {
              const hist = (w._historie || []).map(h => h.bedrag_excl).filter(Boolean);
              const gemiddeld = hist.length ? (hist.reduce((a, b) => a + b, 0) / hist.length) : null;
              return (
                <div key={w.id} className="bg-white border border-amber-300 p-2 flex items-center gap-2" data-testid={`wachtend-${w.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs truncate flex items-center gap-1.5">
                      {w.bron === 'upload_ai' && (w.ai_gelezen
                        ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[#002FA7] text-white text-[9px] font-bold uppercase shrink-0" data-testid={`wachtend-ai-badge-${w.id}`}><Sparkle size={9} weight="fill" /> AI gelezen</span>
                        : <span className="px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-300 text-[9px] font-bold uppercase shrink-0" title={w.ai_fout || ''} data-testid={`wachtend-ai-fout-${w.id}`}>AI niet gelezen — vul aan</span>)}
                      {w.dubbel_van && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold uppercase shrink-0"
                          title={`${w.dubbel_van.leverancier} ${w.dubbel_van.factuurnummer || ''} bestaat al als ${w.dubbel_van.bron === 'kost' ? 'kost' : 'inkomende factuur'} (${w.dubbel_van.datum || '—'}, € ${w.dubbel_van.bedrag ?? '?'})`}
                          data-testid={`wachtend-dubbel-${w.id}`}>
                          <Warning size={9} weight="fill" /> Mogelijk dubbel
                        </span>
                      )}
                      <span className="truncate">{catLabel(w.categorie)} · {w.omschrijving}</span>
                    </div>
                    <div className="text-[10px] text-gray-500 truncate">
                      {w.leverancier || 'Onbekende leverancier'} · {w.datum}
                      {w.referentie ? ` · ${w.referentie}` : ''}
                      {w.filename_bron ? ` · ${w.filename_bron}` : ''}
                      {gemiddeld && hist.length >= 2 ? ` · Gemiddeld vorige: €${gemiddeld.toFixed(2)}` : ''}
                      {hist.length === 1 ? ` · Vorig: €${hist[0].toFixed(2)}` : ''}
                    </div>
                  </div>
                  <div className="text-right font-mono text-sm font-bold text-amber-800">€ {w.bedrag_excl?.toFixed?.(2) || '?'}</div>
                  <div className="flex gap-1">
                    {w.inkomend_doc_id && (
                      <button type="button" className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 text-[11px] font-bold transition-colors" onClick={() => bekijkDocument(w)} title="Open het geüploade document" data-testid={`wachtend-doc-${w.id}`}>
                        <Eye size={12} weight="bold" /> Document
                      </button>
                    )}
                    <button className="btn btn-primary btn-xs inline-flex items-center gap-1" onClick={() => openApproval(w)} data-testid={`goedkeur-${w.id}`}>
                      <CheckCircle size={12} weight="fill" /> Goedkeuren
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-[11px] font-bold transition-colors"
                      onClick={() => openBewerk(w)}
                      title="Bewerk deze concept-kost"
                      data-testid={`wachtend-edit-${w.id}`}
                    >
                      <PencilSimple size={12} weight="bold" /> Bewerk
                    </button>
                    <button className="btn btn-secondary btn-xs inline-flex items-center gap-1 text-red-600" onClick={() => rejectKost(w)} data-testid={`afwijs-${w.id}`}>
                      <XCircle size={12} /> Afwijzen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data && !loading && !data.fout && (
        <>
          {/* KPI-kaarten */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white border border-[#002FA7] p-3" data-testid="kpi-omzet">
              <div className="text-[10px] font-bold text-[#002FA7] uppercase">Verkoop-omzet</div>
              <div className="text-xl font-bold text-gray-900">{fmt(data.verkoop?.omzet || 0)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{data.verkoop?.aantal_documenten ?? data.verkoop?.aantal_facturen ?? 0} verkopen (excl. BTW)</div>
            </div>
            <div className="bg-white border border-emerald-400 p-3" data-testid="kpi-winst">
              <div className="text-[10px] font-bold text-emerald-700 uppercase">Verkoop-winst (netto)</div>
              <div className="text-xl font-bold text-emerald-700" data-testid="cost-report-sales-profit">{data.verkoop?.winst_netto == null ? 'Onvolledig' : fmt(data.verkoop.winst_netto)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">Na commissies</div>
            </div>
            <div className="bg-white border border-red-400 p-3" data-testid="kpi-kosten">
              <div className="text-[10px] font-bold text-red-700 uppercase">Bedrijfskosten</div>
              <div className="text-xl font-bold text-red-700">− {fmt(data.totaal_excl || 0)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{data.aantal_kosten || 0} kosten (excl. BTW)</div>
            </div>
            <div className={`bg-white border-2 p-3 ${(data.netto_resultaat || 0) >= 0 ? 'border-emerald-600' : 'border-red-600'}`} data-testid="kpi-resultaat">
              <div className={`text-[10px] font-bold uppercase ${(data.netto_resultaat || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                Netto resultaat {(data.netto_resultaat || 0) >= 0 ? <TrendUp size={10} className="inline" weight="bold" /> : <TrendDown size={10} className="inline" weight="bold" />}
              </div>
              <div className={`text-2xl font-bold ${(data.netto_resultaat || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="cost-report-net-result">{data.netto_resultaat == null ? 'Onvolledig' : fmt(data.netto_resultaat)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">Marge {data.marge_pct == null ? '—' : `${data.marge_pct.toFixed(1)}%`} op omzet</div>
            </div>
          </div>

          {/* Totaal per categorie */}
          {data.per_categorie && data.per_categorie.length > 0 && (
            <div className="bg-white border border-gray-200 mb-4">
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Totaal per categorie</span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Categorie</th>
                    <th style={{ textAlign: 'right' }}>Aantal</th>
                    <th style={{ textAlign: 'right' }}>Excl. BTW</th>
                    <th style={{ textAlign: 'right' }}>BTW</th>
                    <th style={{ textAlign: 'right' }}>Incl. BTW</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_categorie.map(pc => (
                    <tr key={pc.categorie}>
                      <td><span className="font-semibold">{catLabel(pc.categorie)}</span></td>
                      <td style={{ textAlign: 'right' }}>{pc.aantal}</td>
                      <td style={{ textAlign: 'right' }} className="font-semibold">{fmt(pc.totaal_excl)}</td>
                      <td style={{ textAlign: 'right' }} className="text-gray-500">{fmt(pc.totaal_btw)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(pc.totaal_incl)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#F0F4FF] font-bold">
                    <td className="text-[#002FA7]">TOTAAL</td>
                    <td style={{ textAlign: 'right' }}>{data.aantal_kosten}</td>
                    <td style={{ textAlign: 'right' }} className="text-[#002FA7]">{fmt(data.totaal_excl)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(data.totaal_btw)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(data.totaal_incl)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Detail per kost */}
          <div className="bg-white border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Detail kosten</span>
              <span className="text-[10px] text-gray-500">{data.items?.length || 0} rij(en)</span>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Categorie</th>
                  <th>Omschrijving</th>
                  <th>Leverancier</th>
                  <th style={{ textAlign: 'right' }}>Excl. BTW</th>
                  <th style={{ textAlign: 'right' }}>BTW%</th>
                  <th style={{ textAlign: 'right' }}>Incl. BTW</th>
                  <th style={{ textAlign: 'center' }}>Betaald</th>
                  <th style={{ textAlign: 'center' }} title="Origineel bestand (PDF/afbeelding)">📎</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-8 text-gray-400">Geen kosten geregistreerd in deze periode</td></tr>
                ) : (
                  data.items.map(k => (
                    <tr key={k.id} className={`hover:bg-gray-50 ${k.id === focusKostId ? 'bg-emerald-50 ring-2 ring-inset ring-emerald-400' : ''}`} data-testid={`kost-row-${k.id}`}>
                      <td className="font-mono text-xs">{k.datum}</td>
                      <td>{catLabel(k.categorie)}</td>
                      <td>
                        <div className="font-semibold">{k.omschrijving}</div>
                        {k.referentie && <div className="text-[10px] text-gray-500 font-mono">{k.referentie}</div>}
                        {k.inkomend_doc_id && (
                          <div className="inline-flex items-center gap-1 text-[10px] text-[#002FA7] font-semibold" title="Gekoppeld aan inkomende factuur — factuurdatum is leidend" data-testid={`kost-link-${k.id}`}>
                            <LinkSimple size={10} weight="bold" /> inkomende factuur
                          </div>
                        )}
                      </td>
                      <td>{k.leverancier || '-'}</td>
                      <td style={{ textAlign: 'right' }} className="font-semibold">{fmt(k.bedrag_excl)}</td>
                      <td style={{ textAlign: 'right' }} className="text-gray-500">{k.btw_tarief}%</td>
                      <td style={{ textAlign: 'right' }}>{fmt(k.bedrag_incl)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {k.betaald ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-amber-600">⏳</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {(k.stored || k.inkomend_doc_id) ? (
                          <div className="inline-flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => downloadBestand(k)}
                              className="p-1 text-emerald-600 hover:bg-emerald-50"
                              title={k.stored ? `Download: ${k.filename || 'bestand'}` : 'Download origineel van gekoppelde inkomende factuur'}
                              data-testid={`kost-file-download-${k.id}`}
                            ><Paperclip size={14} weight="fill" /></button>
                            {k.stored && <button
                              type="button"
                              onClick={() => verwijderBestand(k)}
                              className="p-1 text-red-500 hover:bg-red-50"
                              title="Verwijder bestand"
                              data-testid={`kost-file-delete-${k.id}`}
                            ><X size={12} weight="bold" /></button>}
                          </div>
                        ) : (
                          <label
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-gray-500 hover:text-[#002FA7] hover:bg-blue-50 cursor-pointer text-[11px]"
                            title="Upload origineel bestand (PDF/afbeelding)"
                            data-testid={`kost-file-upload-${k.id}`}
                          >
                            <UploadSimple size={12} weight="bold" />
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.heic,.xml" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadBestand(k, e.target.files[0]); e.target.value=''; }} />
                          </label>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-[11px] font-bold transition-colors"
                            onClick={() => openBewerk(k)}
                            title="Bewerk deze kost"
                            data-testid={`kost-edit-${k.id}`}
                          >
                            <PencilSimple size={12} weight="bold" /> Bewerk
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-[11px] font-bold transition-colors"
                            onClick={() => deleteKost(k)}
                            title="Verwijder deze kost"
                            data-testid={`kost-del-${k.id}`}
                          >
                            <Trash size={12} weight="bold" /> Verwijder
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal: Boek vaste kosten voor maand */}
      {showBoekModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="boek-vaste-kosten-modal">
          <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-[#002FA7] text-white">
              <h2 className="font-bold text-sm flex items-center gap-2"><CalendarBlank size={18} weight="fill" /> Boek vaste kosten voor maand</h2>
              <button onClick={() => setShowBoekModal(false)} data-testid="boek-modal-close"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Maand-selectie */}
              <div className="grid grid-cols-2 gap-3 bg-blue-50 border border-blue-200 p-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase">Maand</label>
                  <input type="month" className="field-input" value={boekMaand} onChange={e => setBoekMaand(e.target.value)} data-testid="boek-maand" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase">Boekdatum <span className="text-gray-400 font-normal">(optioneel — default 1e)</span></label>
                  <input type="date" className="field-input" value={boekDatum} onChange={e => setBoekDatum(e.target.value)} placeholder={`${boekMaand}-01`} data-testid="boek-datum" />
                </div>
              </div>

              {/* Sjablonen-lijst */}
              <div className="border border-gray-200">
                <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700 uppercase">Sjablonen ({sjablonen.filter(s => s.actief).length} actief)</span>
                  <button className="btn btn-secondary btn-xs inline-flex items-center gap-1" onClick={() => { setSjabloonForm({ id: null, naam: '', categorie: 'huur', leverancier: '', bedrag_excl_default: '', btw_tarief: 21, sort_order: 100, actief: true, notitie: '' }); setShowSjabloonForm(true); }} data-testid="nieuw-sjabloon-btn"><Plus size={12} weight="bold" /> Nieuw sjabloon</button>
                </div>
                {sjablonen.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500">
                    Nog geen vaste-kost sjablonen aangemaakt.<br />
                    <span className="text-xs">Klik op &ldquo;Nieuw sjabloon&rdquo; om er één toe te voegen (bv. Huur, Elektriciteit, Lonen, RSZ).</span>
                  </div>
                ) : (
                  <table className="w-full text-xs" data-testid="sjablonen-lijst">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left w-8">✓</th>
                        <th className="p-2 text-left">Naam</th>
                        <th className="p-2 text-left">Categorie</th>
                        <th className="p-2 text-right">Standaard</th>
                        <th className="p-2 text-right">Bedrag voor {boekMaand}</th>
                        <th className="p-2 text-center w-16">Beheer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sjablonen.map(s => {
                        const sel = boekSelectie[s.id] || {};
                        const bedragEff = sel.bedrag_override !== '' && sel.bedrag_override !== undefined ? sel.bedrag_override : s.bedrag_excl_default;
                        return (
                          <tr key={s.id} className={`border-t border-gray-100 ${!s.actief ? 'opacity-50' : ''} ${sel.checked ? 'bg-emerald-50' : ''}`} data-testid={`sjabloon-row-${s.id}`}>
                            <td className="p-2">
                              <input type="checkbox" checked={!!sel.checked} onChange={() => toggleSjabloonCheck(s.id)} disabled={!s.actief} data-testid={`sjabloon-check-${s.id}`} />
                            </td>
                            <td className="p-2">
                              <div className="font-semibold">{s.naam}</div>
                              {s.leverancier && <div className="text-[10px] text-gray-500">{s.leverancier}</div>}
                              {!s.actief && <span className="inline-block bg-gray-200 text-gray-600 text-[9px] px-1 rounded">inactief</span>}
                            </td>
                            <td className="p-2 text-gray-600">{catLabel(s.categorie)}</td>
                            <td className="p-2 text-right font-mono text-gray-500">€ {s.bedrag_excl_default.toFixed(2)}</td>
                            <td className="p-2 text-right">
                              <input
                                type="number" step="0.01" min="0"
                                className="field-input text-right font-mono text-xs w-28 inline-block"
                                value={sel.bedrag_override ?? ''}
                                onChange={e => setSjabloonBedrag(s.id, e.target.value)}
                                placeholder={s.bedrag_excl_default.toFixed(2)}
                                disabled={!sel.checked || !s.actief}
                                data-testid={`sjabloon-bedrag-${s.id}`}
                              />
                              <div className="text-[9px] text-gray-400 mt-0.5">te boeken: €{Number(bedragEff || 0).toFixed(2)}</div>
                            </td>
                            <td className="p-2 text-center whitespace-nowrap">
                              <button className="p-1 text-blue-600 hover:bg-blue-50" onClick={() => { setSjabloonForm({ ...s }); setShowSjabloonForm(true); }} title="Bewerk sjabloon" data-testid={`sjabloon-edit-${s.id}`}>
                                <PencilSimple size={12} />
                              </button>
                              <button className="p-1 text-red-600 hover:bg-red-50" onClick={() => deleteSjabloon(s.id)} title="Verwijder sjabloon" data-testid={`sjabloon-del-${s.id}`}>
                                <Trash size={12} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 font-bold">
                      <tr className="border-t-2 border-gray-300">
                        <td className="p-2" colSpan={4}>Totaal geselecteerd:</td>
                        <td className="p-2 text-right font-mono text-[#002FA7]">
                          €{sjablonen.filter(s => boekSelectie[s.id]?.checked).reduce((sum, s) => {
                            const sel = boekSelectie[s.id];
                            const b = sel.bedrag_override !== '' && sel.bedrag_override !== undefined ? Number(sel.bedrag_override) : s.bedrag_excl_default;
                            return sum + (b || 0);
                          }, 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
              <div className="text-[10px] text-gray-500 italic bg-amber-50 border border-amber-200 p-2">
                💡 Reeds geboekte sjablonen voor deze maand worden overgeslagen (duplicaat-preventie op basis van sjabloon-ID + maand).
              </div>
            </div>
            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowBoekModal(false)} disabled={boekBusy}>Annuleren</button>
              <button className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={boekVasteKosten} disabled={boekBusy} data-testid="boek-vaste-btn">
                {boekBusy ? <><ArrowsClockwise size={12} className="animate-spin" /> Boeken…</> : <><CheckCircle size={12} weight="fill" /> Boek geselecteerde kosten</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Sjabloon aanmaken/bewerken */}
      {showSjabloonForm && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="sjabloon-form-modal">
          <div className="bg-white w-full max-w-lg shadow-2xl">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-[#002FA7] text-white">
              <h2 className="font-bold text-sm">{sjabloonForm.id ? '✏️ Sjabloon bewerken' : '➕ Nieuw sjabloon'}</h2>
              <button onClick={() => setShowSjabloonForm(false)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase">Naam *</label>
                <input className="field-input" value={sjabloonForm.naam} onChange={e => setSjabloonForm({ ...sjabloonForm, naam: e.target.value })} placeholder="Bv. Huur magazijn, Elektriciteit, Lonen personeel, RSZ" data-testid="sjabloon-naam" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Categorie</label>
                  <select className="field-input" value={sjabloonForm.categorie} onChange={e => setSjabloonForm({ ...sjabloonForm, categorie: e.target.value })}>
                    {allCats.map(c => <option key={c.code} value={c.code}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Leverancier</label>
                  <input className="field-input" value={sjabloonForm.leverancier} onChange={e => setSjabloonForm({ ...sjabloonForm, leverancier: e.target.value })} placeholder="Verhuurder, Luminus, RSZ, ..." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Standaard bedrag excl.</label>
                  <input type="number" step="0.01" min="0" className="field-input font-mono" value={sjabloonForm.bedrag_excl_default} onChange={e => setSjabloonForm({ ...sjabloonForm, bedrag_excl_default: e.target.value })} placeholder="1500.00" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">BTW %</label>
                  <select className="field-input" value={sjabloonForm.btw_tarief} onChange={e => setSjabloonForm({ ...sjabloonForm, btw_tarief: Number(e.target.value) })}>
                    <option value={0}>0%</option><option value={6}>6%</option><option value={12}>12%</option><option value={21}>21%</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Sorteer</label>
                  <input type="number" className="field-input" value={sjabloonForm.sort_order} onChange={e => setSjabloonForm({ ...sjabloonForm, sort_order: Number(e.target.value) })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={!!sjabloonForm.actief} onChange={e => setSjabloonForm({ ...sjabloonForm, actief: e.target.checked })} id="sjabloon-actief" />
                <label htmlFor="sjabloon-actief" className="text-xs">Actief — zichtbaar in boekmodal</label>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase">Notitie (optioneel)</label>
                <textarea className="field-input text-xs" rows={2} value={sjabloonForm.notitie} onChange={e => setSjabloonForm({ ...sjabloonForm, notitie: e.target.value })} placeholder="Bv. 'Contractnummer 12345 · verhoging elke januari met index'" />
              </div>
            </div>
            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowSjabloonForm(false)}>Annuleren</button>
              <button className="btn btn-primary btn-sm" onClick={saveSjabloon} data-testid="sjabloon-save">Opslaan</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nieuwe/Bewerk kost */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" data-testid="kosten-modal">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-[#002FA7] text-white">
              <h2 className="font-bold text-sm">{editing ? '✏️ Kost bewerken' : '💰 Nieuwe kost registreren'}</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Datum *{form.inkomend_doc_id && <span className="ml-1 normal-case font-normal text-[#002FA7]">= factuurdatum</span>}</label>
                  <input type="date" className={`field-input ${form.inkomend_doc_id ? 'bg-blue-50 text-[#002FA7] font-semibold' : ''}`} value={form.datum} readOnly={!!form.inkomend_doc_id} title={form.inkomend_doc_id ? 'Factuurdatum van de gekoppelde inkomende factuur is leidend' : ''} onChange={e => setForm({ ...form, datum: e.target.value })} data-testid="kost-form-datum" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Categorie *</label>
                  <select className="field-input" value={form.categorie} onChange={e => { setForm({ ...form, categorie: e.target.value }); setCatHint(h => h ? { ...h, toegepast: false } : h); }} data-testid="kost-form-cat">
                    {allCats.map(c => <option key={c.code} value={c.code}>{c.icon} {c.label}</option>)}
                  </select>
                  {catHint && (
                    <div className="mt-1 text-[10px] text-emerald-700 flex items-center gap-1.5 flex-wrap" data-testid="kost-cat-hint">
                      <Bookmark size={11} weight="fill" />
                      {catHint.toegepast
                        ? <>Automatisch: <strong>{catLabel(catHint.categorie)}</strong> — geleerd van {catHint.aantal} eerdere kost(en) van {catHint.leverancier}</>
                        : <>Eerder gebruikt voor {catHint.leverancier}: <strong>{catLabel(catHint.categorie)}</strong> ({catHint.aantal}×)
                          {catHint.categorie !== form.categorie && <button type="button" className="underline text-[#002FA7]" onClick={() => { setForm(p => ({ ...p, categorie: catHint.categorie })); setCatHint(h => ({ ...h, toegepast: true })); }} data-testid="kost-cat-hint-apply">toepassen</button>}</>}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase">Omschrijving *</label>
                <input type="text" className="field-input" value={form.omschrijving} onChange={e => setForm({ ...form, omschrijving: e.target.value })} placeholder="Bv. Huur kantoor maart 2026" data-testid="kost-form-omschr" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Leverancier</label>
                  <input type="text" className="field-input" value={form.leverancier} onChange={e => setForm({ ...form, leverancier: e.target.value })} onBlur={e => zoekGeleerdeCategorie(e.target.value)} placeholder="Bv. Luminus" data-testid="kost-form-lev" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Referentie / factuurnr</label>
                  <input type="text" className="field-input" value={form.referentie} onChange={e => setForm({ ...form, referentie: e.target.value })} placeholder="Bv. F-2026-456" data-testid="kost-form-ref" />
                </div>
              </div>
              <div className="bg-blue-50 p-3 border border-blue-200 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-gray-600 uppercase">Ingevoerd bedrag is</span>
                  <div className="inline-flex border border-[#002FA7]/40" data-testid="kost-form-basis">
                    {['excl', 'incl'].map(b => (
                      <button key={b} type="button" onClick={() => setForm({ ...form, bedrag_basis: b })}
                        className={`px-2.5 py-1 text-[11px] font-bold ${form.bedrag_basis === b ? 'bg-[#002FA7] text-white' : 'bg-white text-[#002FA7] hover:bg-blue-100'}`}
                        data-testid={`kost-form-basis-${b}`}>
                        {b === 'excl' ? 'Excl. BTW' : 'Incl. BTW'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-gray-600 uppercase">Bedrag {form.bedrag_basis === 'incl' ? 'incl.' : 'excl.'} BTW * €</label>
                    {form.bedrag_basis === 'incl'
                      ? <input type="number" step="0.01" className="field-input font-bold text-lg" value={form.bedrag_incl} onChange={e => setForm({ ...form, bedrag_incl: e.target.value })} data-testid="kost-form-incl" />
                      : <input type="number" step="0.01" className="field-input font-bold text-lg" value={form.bedrag_excl} onChange={e => setForm({ ...form, bedrag_excl: e.target.value })} data-testid="kost-form-excl" />}
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-600 uppercase">BTW %</label>
                    <select className="field-input" value={form.btw_tarief} onChange={e => setForm({ ...form, btw_tarief: parseFloat(e.target.value) })} data-testid="kost-form-btw">
                      <option value="0">0% (vrijgesteld)</option>
                      <option value="6">6%</option>
                      <option value="12">12%</option>
                      <option value="21">21%</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-600 uppercase">{form.bedrag_basis === 'incl' ? 'Excl. BTW' : 'Incl. BTW'}</label>
                    <div className="field-input bg-gray-100 text-right font-mono" data-testid="kost-form-berekend">
                      € {(form.bedrag_basis === 'incl' ? bedragExclBerekend : bedragInclBerekend).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-2 border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={form.betaald} onChange={e => setForm({ ...form, betaald: e.target.checked })} data-testid="kost-form-betaald" />
                  <span className="text-xs font-semibold">Al betaald</span>
                </label>
                {form.betaald && (
                  <div>
                    <label className="text-[11px] font-bold text-gray-600 uppercase">Betaaldatum</label>
                    <input type="date" className="field-input" value={form.betaaldatum} onChange={e => setForm({ ...form, betaaldatum: e.target.value })} data-testid="kost-form-betaaldt" />
                  </div>
                )}
              </div>
              <KostInkomendKoppel
                value={form.inkomend_doc_id}
                kostId={editing?.id}
                onOvernemen={neemFactuurOver}
                onOntkoppel={() => setForm(p => ({ ...p, inkomend_doc_id: '' }))}
              />
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase">Commentaar</label>
                <textarea className="field-input" rows="2" value={form.commentaar} onChange={e => setForm({ ...form, commentaar: e.target.value })} data-testid="kost-form-cmt" />
              </div>

              {/* Terugkerend-sectie */}
              <div className="bg-indigo-50 border border-indigo-200 p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="kost-form-terugkerend-label">
                  <input type="checkbox" checked={form.terugkerend} onChange={e => setForm({ ...form, terugkerend: e.target.checked })} className="w-4 h-4" data-testid="kost-form-terugkerend" />
                  <span className="text-xs font-bold text-indigo-900">
                    <ArrowsClockwise size={14} weight="bold" className="inline mr-1" />
                    Terugkerende kost (automatisch concept-kosten genereren)
                  </span>
                </label>
                {form.terugkerend && (
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-indigo-200">
                    <div>
                      <label className="text-[10px] font-bold text-gray-600 uppercase">Frequentie</label>
                      <select className="field-input text-xs" value={form.terugkerend_interval} onChange={e => setForm({ ...form, terugkerend_interval: e.target.value })} data-testid="kost-form-interval">
                        <option value="maandelijks">Maandelijks</option>
                        <option value="jaarlijks">Jaarlijks</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-600 uppercase">Dag v/d maand</label>
                      <select className="field-input text-xs" value={form.terugkerend_dag} onChange={e => setForm({ ...form, terugkerend_dag: parseInt(e.target.value) })} data-testid="kost-form-dag">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}{d === 1 ? 'e (1ste)' : d === 5 ? 'e (vervaldag huur)' : d === 15 ? 'e (mid-maand)' : 'e'}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-600 uppercase">Uur</label>
                      <select className="field-input text-xs" value={form.terugkerend_uur} onChange={e => setForm({ ...form, terugkerend_uur: parseInt(e.target.value) })} data-testid="kost-form-uur">
                        {Array.from({ length: 24 }, (_, i) => i).map(u => (
                          <option key={u} value={u}>{String(u).padStart(2, '0')}:00</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3 text-[10px] text-indigo-800 bg-white p-1.5 border border-indigo-200">
                      💡 Concept-kost wordt automatisch aangemaakt op <strong>dag {form.terugkerend_dag}</strong> om <strong>{String(form.terugkerend_uur).padStart(2, '0')}:00</strong> ({form.terugkerend_interval === 'jaarlijks' ? 'jaarlijks' : 'elke maand'}). Jij keurt goed met evt. aangepast bedrag.
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); resetForm(); }}>Annuleren</button>
              <button className="btn btn-primary btn-sm" onClick={saveKost} data-testid="kost-form-save">{editing ? 'Bijwerken' : 'Opslaan'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Goedkeur concept-kost (bedrag aanpasbaar) */}
      {approving && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-xl" data-testid="approval-modal">
            <div className="p-4 border-b border-amber-400 flex items-center justify-between bg-amber-500 text-white">
              <h2 className="font-bold text-sm inline-flex items-center gap-2">
                <CheckCircle size={16} weight="fill" /> Kost goedkeuren
              </h2>
              <button onClick={() => setApproving(null)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              {approving.dubbel_van && (
                <div className="bg-red-50 border-2 border-red-400 p-2.5 text-[11px] text-red-900" data-testid="approval-dubbel">
                  <div className="font-bold flex items-center gap-1"><Warning size={13} weight="fill" /> Mogelijk dubbele factuur</div>
                  <div className="mt-1">
                    <strong>{approving.dubbel_van.leverancier}</strong> · nr <strong>{approving.dubbel_van.factuurnummer || '—'}</strong> bestaat al als {approving.dubbel_van.bron === 'kost' ? 'kost' : 'inkomende factuur'}
                    {approving.dubbel_van.datum ? ` van ${approving.dubbel_van.datum}` : ''}{approving.dubbel_van.bedrag != null ? ` (€ ${Number(approving.dubbel_van.bedrag).toFixed(2)})` : ''}.
                    Controleer vóór u goedkeurt — of klik "Afwijzen" om dit duplicaat te verwijderen.
                  </div>
                  {approving.dubbel_van.bron === 'kost' && (
                    <a href={`/kosten-rapport?kost=${approving.dubbel_van.id}&datum=${approving.dubbel_van.datum || ''}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-1.5 px-2 py-1 bg-red-600 text-white font-bold hover:bg-red-700" data-testid="approval-dubbel-open">Open bestaande kost →</a>
                  )}
                </div>
              )}
              {approving.bron === 'upload_ai' ? (
                <div className={`border p-2 text-[11px] flex items-start justify-between gap-2 ${approving.ai_gelezen ? 'bg-[#F4F7FF] border-[#002FA7]/30 text-[#002FA7]' : 'bg-red-50 border-red-300 text-red-800'}`} data-testid="approval-ai-info">
                  <span>
                    {approving.ai_gelezen
                      ? <><Sparkle size={12} weight="fill" className="inline mr-1" /><strong>AI heeft deze factuur gelezen</strong>{approving.filename_bron ? ` (${approving.filename_bron})` : ''}. Controleer leverancier, datum, bedrag en categorie en klik op "Goedkeuren".</>
                      : <><strong>AI kon deze factuur niet betrouwbaar lezen</strong>{approving.ai_fout ? ` — ${approving.ai_fout}` : ''}. Vul de gegevens zelf aan en keur dan goed.</>}
                  </span>
                  {approving.inkomend_doc_id && (
                    <button type="button" className="shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-bold" onClick={() => bekijkDocument(approving)} data-testid="approval-doc">
                      <Eye size={12} weight="bold" /> Document bekijken
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-300 p-2 text-[11px] text-amber-900">
                  🔄 <strong>Auto-gegenereerd</strong> op basis van vorige terugkerende kost. Pas het bedrag aan als nodig en klik op "Goedkeuren" om definitief te maken.
                </div>
              )}
              <div>
                <label className="text-[11px] font-bold text-gray-600 uppercase">Omschrijving</label>
                <input type="text" className="field-input" value={approving.omschrijving} onChange={e => setApproving({ ...approving, omschrijving: e.target.value })} data-testid="approval-omschr" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Categorie</label>
                  <select className="field-input" value={approving.categorie} onChange={e => setApproving({ ...approving, categorie: e.target.value })} data-testid="approval-cat">
                    {!allCats.some(c => c.code === approving.categorie) && <option value={approving.categorie}>{approving.categorie}</option>}
                    {allCats.map(c => <option key={c.code} value={c.code}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Factuurnummer / referentie</label>
                  <input type="text" className="field-input font-mono" value={approving.referentie} onChange={e => setApproving({ ...approving, referentie: e.target.value })} data-testid="approval-ref" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Datum</label>
                  <input type="date" className="field-input" value={approving.datum} onChange={e => setApproving({ ...approving, datum: e.target.value })} data-testid="approval-datum" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Leverancier</label>
                  <input type="text" className="field-input" value={approving.leverancier} onChange={e => setApproving({ ...approving, leverancier: e.target.value })} data-testid="approval-lev" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 bg-emerald-50 p-3 border border-emerald-300">
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">
                    Bedrag excl. BTW €
                    {approving._historie?.length > 0 && (
                      <span className="text-amber-700 ml-1" title="Vorige bedragen voor referentie">
                        (was €{approving._historie.map(h => h.bedrag_excl?.toFixed?.(2)).join(', €')})
                      </span>
                    )}
                  </label>
                  <input type="number" step="0.01" className="field-input font-bold text-lg" value={approving.bedrag_excl} onChange={e => setApproving({ ...approving, bedrag_excl: e.target.value })} autoFocus data-testid="approval-bedrag" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">BTW %</label>
                  <select className="field-input" value={approving.btw_tarief} onChange={e => setApproving({ ...approving, btw_tarief: parseFloat(e.target.value) })} data-testid="approval-btw">
                    <option value="0">0%</option>
                    <option value="6">6%</option>
                    <option value="12">12%</option>
                    <option value="21">21%</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-600 uppercase">Incl. BTW</label>
                  <div className="field-input bg-gray-100 text-right font-mono">
                    € {approving.bedrag_excl ? (parseFloat(approving.bedrag_excl) * (1 + parseFloat(approving.btw_tarief || 0) / 100)).toFixed(2) : '0.00'}
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 p-2 border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={approving.betaald} onChange={e => setApproving({ ...approving, betaald: e.target.checked })} data-testid="approval-betaald" />
                <span className="text-xs font-semibold">Al betaald</span>
              </label>
            </div>
            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => setApproving(null)}>Annuleren</button>
              <button className="btn btn-primary btn-sm bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1" onClick={approveKost} data-testid="approval-save">
                <CheckCircle size={14} weight="fill" /> Goedkeuren & toevoegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
